import { useEffect, useRef } from 'react';
import { isInIframe } from '@shared/utils/isInIframe';
import { useQueryParamHelper } from '@shared/core/queryString';
import type { SupportedBrannanHeaderPhotoLayout } from '@shared/types/WebsiteHeaderSettings.types';
import { LazyCenterLayout } from './layouts/Center.lazy';
import { LazyFullWidthLayout } from './layouts/FullWidth.lazy';
import { LazyPageWidthLayout } from './layouts/PageWidth.lazy';
import { LazyNoPhotoLayout } from './layouts/NoPhoto.lazy';
import { LazyPhotoArchLayout } from './layouts/PhotoArch.lazy';
import { LazyPhotoDuoLeftLayout } from './layouts/PhotoDuoLeft.lazy';
import { LazyPhotoDuoRightLayout } from './layouts/PhotoDuoRight.lazy';
import { LazyPhotoMarqueeLayout } from './layouts/PhotoMarquee.lazy';
import { LazyPhotoMarqueePageWidthLayout } from './layouts/PhotoMarqueePageWidth.lazy';
import { LazyPhotoSliderLayout } from './layouts/PhotoSlider.lazy';
import { LazyPhotoSliderPageWidthLayout } from './layouts/PhotoSliderPageWidth.lazy';
import { LazyPhotoTrioLayout } from './layouts/PhotoTrio.lazy';
import { LazyPhotoTrioPageWidthLayout } from './layouts/PhotoTrioPageWidth.lazy';
import { LazyMonogramLayout } from './layouts/Monogram.lazy';
import { LazyVideoArchLayout } from './layouts/VideoArchLayout.lazy';
import { LazyPhotoBackgroundLayout } from './layouts/PhotoBackground.lazy';
import { LazyPhotoBackgroundMonogramLayout } from './layouts/PhotoBackgroundMonogram.lazy';
import { LazyVideoBackgroundLayout } from './layouts/VideoBackground.lazy';
import { LazyVideoBackgroundMonogramLayout } from './layouts/VideoBackgroundMonogram.lazy';
import { LazyVideoBackgroundHotfixLayout } from './layouts/VideoBackgroundHotfix.lazy';
import type { LoadableComponent } from '@loadable/component';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PreloadableComponent = LoadableComponent<any>;

// Keyed by SupportedBrannanHeaderPhotoLayout so TypeScript will error if a new
// layout variant is added to the type but no corresponding lazy component is wired up here.
const BRANNAN_HEADER_PRELOAD_MAP: Record<SupportedBrannanHeaderPhotoLayout, PreloadableComponent> = {
  center: LazyCenterLayout,
  fullWidth: LazyFullWidthLayout,
  pageWidth: LazyPageWidthLayout,
  noPhoto: LazyNoPhotoLayout,
  photoArch: LazyPhotoArchLayout,
  photoDuoLeft: LazyPhotoDuoLeftLayout,
  photoDuoRight: LazyPhotoDuoRightLayout,
  photoMarquee: LazyPhotoMarqueeLayout,
  photoMarqueePageWidth: LazyPhotoMarqueePageWidthLayout,
  photoSlider: LazyPhotoSliderLayout,
  photoSliderPageWidth: LazyPhotoSliderPageWidthLayout,
  photoTrio: LazyPhotoTrioLayout,
  photoTrioPageWidth: LazyPhotoTrioPageWidthLayout,
  monogram: LazyMonogramLayout,
  videoArch: LazyVideoArchLayout,
  photoBackground: LazyPhotoBackgroundLayout,
  photoBackgroundMonogram: LazyPhotoBackgroundMonogramLayout,
  videoBackground: LazyVideoBackgroundLayout,
  videoBackgroundMonogram: LazyVideoBackgroundMonogramLayout,
};

// videoBackgroundHotfix shares the 'photoBackground' presentationLayout value but renders
// a different component when the video background feature flag + event payload are active.
// It is not a distinct SupportedBrannanHeaderPhotoLayout key, so it is tracked separately.
const BRANNAN_HEADER_PRELOAD_EXTRAS: PreloadableComponent[] = [LazyVideoBackgroundHotfixLayout];

/**
 * Preloads all Brannan header layout chunks when running inside the website
 * designer preview iframe. Admins can switch between any header variant, so
 * fetching them eagerly eliminates the per-switch flicker that lazy loading
 * would otherwise cause.
 *
 * Returns null — renders nothing.
 */
export const HeaderPreloader = (): null => {
  const { getValueString } = useQueryParamHelper();
  const hasPreloaded = useRef(false);

  const shouldPreload = isInIframe() && getValueString('ctx') === 'websiteDesigner';

  useEffect(() => {
    if (hasPreloaded.current) return;
    if (!shouldPreload) return;

    hasPreloaded.current = true;
    Object.values(BRANNAN_HEADER_PRELOAD_MAP).forEach(component => component?.preload());
    BRANNAN_HEADER_PRELOAD_EXTRAS.forEach(component => component?.preload());
  }, [shouldPreload]);

  return null;
};
